import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

import { useBizyExternalOpenStore } from '../store/use-bizy-external-open-store';
import { useBizyPageContextStore } from '../store/use-bizy-page-context-store';
import { BIZY_ROUTE_TRIGGER, BIZY_SETTINGS_SECTIONS, useBizySidePanelStore, type BizySettingsSection } from '../store/use-bizy-side-panel-store';

// ── URL model (BIZY-680): Bizy is a `?bizy=` search param on the CURRENT page ──
//   ?bizy=chat            → panel open, chat home
//   ?bizy=chat-with-bizy  → an active conversation (id in history.state.bizyConversationId)
//   ?bizy=noticed.<slug>  → person detail in "Bizy noticed" (id in history.state.bizyPersonId)
//   ?bizy=settings        → the settings modal on its General tab
//   ?bizy=settings.<slug> → the settings modal on a specific tab (BIZY-701)
//
// The person separator is `.` (not `/`) so the value stays readable in the
// address bar — `/` would be percent-encoded to `%2F` by URLSearchParams.
//
// The param value is COSMETIC (the person slug is derived from the display name);
// the sensitive id rides in React Router navigation state (history.state), never
// the URL. Because opening Bizy is a search-param change on the SAME pathname,
// the underlying page never unmounts — no backdrop machinery needed.
//
// Legacy aliases kept for back-compat: `?bizy=open` → chat, `?bizy=fullscreen` →
// chat + fullscreen. (The `bizy_prompt` / `bizy_conversation` / `bizy_settings`
// deep-link params are owned by the deep-link hooks; the `bizy` param is owned here.)

export const BIZY_SEARCH_PARAM = 'bizy';
const CHAT_VALUE = 'chat';
const CONVERSATION_VALUE = 'chat-with-bizy';
const PERSON_PREFIX = 'noticed.';
const SETTINGS_VALUE = 'settings';
const SETTINGS_PREFIX = 'settings.';

// The URL slug for a settings tab is human-facing and NOT always the internal
// section id: the Connectors tab's internal id is `connections` but its slug is
// `connectors`. Only that one differs; every other slug is identity. Deriving both
// directions from a single override map (over the store's canonical section list)
// keeps the translation in one place and can't drift out of sync.
const SECTION_SLUG_OVERRIDES: Partial<Record<BizySettingsSection, string>> = { connections: 'connectors' };
const sectionToSlug = (section: BizySettingsSection): string => SECTION_SLUG_OVERRIDES[section] ?? section;
const slugToSection = (slug: string): BizySettingsSection => BIZY_SETTINGS_SECTIONS.find((section) => sectionToSlug(section) === slug) ?? 'general';

export interface BizyRouteState {
	bizyConversationId?: string;
	bizyPersonId?: string;
	// The specific "Bizy noticed" moment (card) shown when this entry was pushed,
	// so Back/Forward restores the exact card rather than re-defaulting to the
	// newest. In history state, not the URL — the URL carries only the cosmetic
	// slug. Absent on cold loads / entries pushed before a moment resolved, where
	// the person view's newest-moment default applies (self-consistent: that entry
	// displayed the default too).
	bizyObservedMomentId?: string;
	// How many history entries this Bizy visit has pushed up to AND INCLUDING the
	// entry carrying this state (open = 1, each conversation/person push = prior
	// depth + 1). Closing pops exactly that many, landing on the pre-open page.
	// Written into every entry so browser Back/Forward mid-stack restores the right
	// count (see `depthRef` — the live counter is driven by a synchronous ref, and
	// re-synced FROM this per-entry value on inbound POP, because reading it back
	// from the async `location` during rapid pushes lags and undercounts; BIZY-703).
	// Absent (0) on cold loads / pasted links / URL-driven opens, where close
	// replace-strips instead.
	bizyDepth?: number;
}

export type BizyRouteTarget =
	| { kind: 'chat' }
	| { kind: 'conversation' }
	| { kind: 'person'; slug: string }
	| { kind: 'settings'; section: BizySettingsSection }
	| { kind: 'open' } // legacy alias → chat
	| { kind: 'fullscreen' }; // legacy alias → chat + fullscreen

/**
 * Slugify arbitrary text into a lowercase, hyphenated, ascii-safe segment.
 * Cosmetic only — never reversed.
 */
export function slugify(input: string | null | undefined): string {
	if (!input) return '';
	// NFKD decomposes accented letters into a base ascii letter + a combining
	// mark; the [^a-z0-9] filter then drops the mark, keeping the base letter
	// (e.g. "José" → "jose"). Cosmetic only, so we don't need perfect fidelity.
	return input
		.normalize('NFKD')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.slice(0, 60)
		.replace(/-+$/, '');
}

/** Person slug from a display name: first name + last initial (e.g. `jordan-m`). */
export function personSlug(name: string | null | undefined): string {
	if (!name) return '';
	const parts = name.trim().split(/\s+/);
	const first = parts[0] ?? '';
	const lastInitial = parts.length > 1 ? parts[parts.length - 1][0] : '';
	return slugify(lastInitial ? `${first} ${lastInitial}` : first);
}

/** Parse the `?bizy=` param into a route target, or null when absent/unrecognized. */
export function parseBizyParam(search: string): BizyRouteTarget | null {
	const value = new URLSearchParams(search).get(BIZY_SEARCH_PARAM);
	if (!value) return null;
	if (value === CHAT_VALUE) return { kind: 'chat' };
	if (value === CONVERSATION_VALUE) return { kind: 'conversation' };
	if (value === 'open') return { kind: 'open' };
	if (value === 'fullscreen') return { kind: 'fullscreen' };
	if (value === SETTINGS_VALUE) return { kind: 'settings', section: 'general' };
	if (value.startsWith(SETTINGS_PREFIX)) {
		// `settings.general` is accepted as an alias for the bare `settings`; any
		// unknown/invalid slug falls back to the General tab.
		const slug = value.slice(SETTINGS_PREFIX.length);
		return { kind: 'settings', section: slugToSection(slug) };
	}
	if (value.startsWith(PERSON_PREFIX)) {
		const slug = value.slice(PERSON_PREFIX.length);
		if (slug) return { kind: 'person', slug };
	}
	return null;
}

// Param builders (the value that goes into `?bizy=`).
export const bizyChatValue = () => CHAT_VALUE;
export const bizyConversationValue = () => CONVERSATION_VALUE;
export const bizyPersonValue = (name?: string | null) => `${PERSON_PREFIX}${personSlug(name) || 'person'}`;
/** Canonical settings param: bare `settings` for General, `settings.<slug>` otherwise. */
export const bizySettingsValue = (section: BizySettingsSection) => (section === 'general' ? SETTINGS_VALUE : `${SETTINGS_PREFIX}${sectionToSlug(section)}`);

/** Return the given search string with `bizy` set to value, other params preserved. */
function searchWithBizy(search: string, value: string): string {
	const params = new URLSearchParams(search);
	params.set(BIZY_SEARCH_PARAM, value);
	return `?${params.toString()}`;
}

/** Return the given search string with `bizy` removed, other params preserved. */
function searchWithoutBizy(search: string): string {
	const params = new URLSearchParams(search);
	params.delete(BIZY_SEARCH_PARAM);
	const next = params.toString();
	return next ? `?${next}` : '';
}

// Only explicit user opens/closes drive the URL. Auto-open (preference, trial,
// navbar restore), deep links, and notification peeks keep the panel as an
// overlay on the current page without writing the param.
const USER_INTENT_TRIGGERS = new Set(['user', 'keyboard']);

function readRouteState(state: unknown): BizyRouteState {
	return (state as BizyRouteState | null) ?? {};
}

/**
 * Sync between the Bizy side-panel store and the `?bizy=` search param (BIZY-680).
 * Mounted from {@link useBizyLayout} for the member web app only.
 *
 * The STORE is the single source of truth for the open panel's view (settings
 * section, viewed person, active conversation, modal-open). The two effects mirror
 * in one direction each, and never fight, because:
 *
 *   Store → route: reflects the store into the URL — push one entry when a view
 *   opens (so browser Back collapses it), replace in place when it changes, pop or
 *   replace-collapse when it closes. Only user/keyboard-initiated opens write.
 *
 *   Route → store: applies the URL to the store ONLY on genuine inbound navigation
 *   — browser back/forward (POP) or a first load / external open while the panel is
 *   closed. It NEVER runs on the PUSH/REPLACE writes we make above, so it can't
 *   stomp a section/view the user just changed. On an inbound target it derives the
 *   full desired view from the URL and applies it wholesale; a non-settings target
 *   lands on a view (showChat / viewPerson) that closes the settings modal, so Back
 *   out of settings dismisses it with no special-casing.
 *
 * The param value is COSMETIC (person slug from the display name); the sensitive id
 * rides in navigation state. Because every write keeps `location.pathname`, the page
 * under the overlay never unmounts.
 */
export function useBizyRouteSync(enabled: boolean) {
	const location = useLocation();
	const navigate = useNavigate();
	// POP marks genuine inbound navigation (first load, browser back/forward).
	// PUSH/REPLACE while the panel is open are our own store→route writes.
	const navigationType = useNavigationType();

	const isOpen = useBizySidePanelStore((state) => state.isOpen);
	const viewedPerson = useBizySidePanelStore((state) => state.viewedPerson);
	const activeConversationId = useBizySidePanelStore((state) => state.activeConversationId);
	const isSettingsModalOpen = useBizySidePanelStore((state) => state.isSettingsModalOpen);
	const settingsSection = useBizySidePanelStore((state) => state.settingsSection);
	const lastTrigger = useBizySidePanelStore((state) => state.lastTrigger);

	// A conversation deep link (param or `?bizy_conversation=`) queues a pending
	// load before `activeConversationId` catches up; treat that as the intended
	// conversation so store→route doesn't collapse the URL mid-load.
	const pendingConversationId = useBizyExternalOpenStore((state) => state.pendingRequest?.conversationId);

	// True while the current open is tracked by a `?bizy=` param (a param we wrote,
	// or a direct-nav / back-forward open), so removing the param (browser Back to a
	// param-less entry) closes the panel.
	const urlDrivenOpenRef = useRef(false);

	const prevIsOpenRef = useRef(isOpen);
	const prevConversationIdRef = useRef(activeConversationId);
	const prevPersonIdRef = useRef(viewedPerson?.id ?? null);
	const prevMomentIdRef = useRef(viewedPerson?.observedMomentId ?? null);
	const prevSettingsOpenRef = useRef(isSettingsModalOpen);
	const prevSearchRef = useRef(location.search);
	// Live depth of the current Bizy visit, incremented SYNCHRONOUSLY on each push so
	// rapid consecutive pushes count correctly even while `location` (and thus the
	// per-entry `bizyDepth`) lags behind under the async data router. Re-synced from
	// the landed entry's `bizyDepth` on inbound POP / first load, reset to 0 when the
	// param is gone. The user-close pops `navigate(-depthRef.current)`. (BIZY-703)
	const depthRef = useRef(0);

	// ── Route → store ──
	useEffect(() => {
		if (!enabled) return;

		const { pathname, search } = location;
		const routeState = readRouteState(location.state);
		const prevSearch = prevSearchRef.current;
		prevSearchRef.current = search;

		const target = parseBizyParam(search);
		const store = useBizySidePanelStore.getState();

		if (!target) {
			// No `?bizy=` param → no Bizy-visit depth to carry.
			depthRef.current = 0;
			// If this open was URL-driven (e.g. browser Back removed the param), close
			// the panel so state follows the URL.
			const hadBizyParam = parseBizyParam(prevSearch) !== null;
			if (hadBizyParam && urlDrivenOpenRef.current && store.isOpen) {
				urlDrivenOpenRef.current = false;
				store.close(BIZY_ROUTE_TRIGGER);
			}
			return;
		}

		// Apply the URL to the store ONLY for genuine inbound navigation: browser
		// back/forward (POP) or a first load / external open while the panel is closed.
		// Our own store→route writes are PUSH/REPLACE while the panel is already open;
		// skipping them keeps the store authoritative so the URL can't stomp a
		// just-changed tab or view (the two-way-sync fights this design removes).
		if (navigationType !== 'POP' && store.isOpen) return;

		// Genuine inbound navigation: re-sync the live depth counter from the entry we
		// landed on, so store-driven pushes from here increment off the correct base
		// and the close pops the right count. (Our own writes returned above, so this
		// never runs on the lagging location of a push we just made.)
		depthRef.current = routeState.bizyDepth ?? 0;

		urlDrivenOpenRef.current = true;

		// Legacy aliases normalize onto the steady-state param.
		if (target.kind === 'open' || target.kind === 'fullscreen') {
			if (!store.isOpen) store.open(BIZY_ROUTE_TRIGGER, { isFullscreen: target.kind === 'fullscreen' });
			else if (target.kind === 'fullscreen' && !store.isFullscreen) useBizySidePanelStore.setState({ isFullscreen: true });
			store.showChat();
			navigate({ pathname, search: searchWithBizy(search, CHAT_VALUE) }, { replace: true, state: routeState });
			return;
		}

		if (target.kind === 'settings') {
			// Open the panel and surface the settings modal on the requested tab. The
			// modal renders over chat, so showSettings also lands the view on chat.
			if (!store.isOpen) store.open(BIZY_ROUTE_TRIGGER);
			store.showSettings(target.section);
			return;
		}

		if (target.kind === 'person') {
			const personId = routeState.bizyPersonId;
			if (!store.isOpen) store.open(BIZY_ROUTE_TRIGGER);
			if (!personId) {
				// No usable state (cold load / pasted link): open Bizy without a
				// specific person. Never reverse the slug.
				store.showChat();
				return;
			}
			// Restore the exact card this entry was pushed with. A missing moment
			// (cold load / entry pushed before the moment resolved) passes '' and
			// BizyPersonView defaults to the newest once moments load — in that case
			// a concrete moment already in the store for the same person is left
			// alone rather than re-cleared. Same person at a DIFFERENT concrete
			// moment (Back between two cards of one person) must update the card.
			const observedMomentId = routeState.bizyObservedMomentId ?? '';
			const personChanged = store.viewedPerson?.id !== personId;
			const momentChanged = observedMomentId !== '' && store.viewedPerson?.observedMomentId !== observedMomentId;
			if (personChanged || momentChanged) {
				store.viewPerson({ id: personId, name: '', observedMomentId: observedMomentId });
			} else if (store.isSettingsModalOpen) {
				// Same person, but the settings modal was open over it (browser Back out
				// of settings): dismiss the modal so the person view underneath shows.
				store.closeSettingsModal();
			}
			return;
		}

		if (target.kind === 'conversation') {
			const conversationId = routeState.bizyConversationId;
			if (!store.isOpen) store.open(BIZY_ROUTE_TRIGGER);
			store.showChat();
			if (!conversationId) return; // no usable state → chat home, don't resolve.
			const alreadyOnConversation = store.activeConversationId === conversationId;
			const alreadyLoading = pendingConversationId === conversationId;
			if (!alreadyOnConversation && !alreadyLoading) {
				useBizyExternalOpenStore.getState().setPendingRequest({
					source: 'url_route',
					pagePath: pathname,
					pageContext: useBizyPageContextStore.getState().context.pageType,
					conversationId,
					autoSend: false,
				});
			}
			return;
		}

		// chat
		if (!store.isOpen) store.open(BIZY_ROUTE_TRIGGER);
		store.showChat();
	}, [enabled, location, pendingConversationId, navigate, navigationType]);

	// ── Store → route ──
	useEffect(() => {
		if (!enabled) return;

		const { pathname, search } = location;
		const routeState = readRouteState(location.state);
		const target = parseBizyParam(search);
		const onBizyParam = target !== null;

		const prevIsOpen = prevIsOpenRef.current;
		const prevConversationId = prevConversationIdRef.current;
		const prevPersonId = prevPersonIdRef.current;
		const prevMomentId = prevMomentIdRef.current;
		const personId = viewedPerson?.id ?? null;
		const momentId = viewedPerson?.observedMomentId ?? null;
		const prevSettingsOpen = prevSettingsOpenRef.current;
		prevIsOpenRef.current = isOpen;
		prevConversationIdRef.current = activeConversationId;
		prevPersonIdRef.current = personId;
		prevMomentIdRef.current = momentId;
		prevSettingsOpenRef.current = isSettingsModalOpen;

		// Open transition: a user-initiated open writes the param onto the current
		// page (pathname preserved) at depth 1. If a conversation is already active,
		// write the conversation param + id so reopening restores the thread's URL.
		if (isOpen && !prevIsOpen) {
			if (!onBizyParam && USER_INTENT_TRIGGERS.has(lastTrigger ?? '')) {
				// The param now tracks the open, so a later Back to the param-less entry
				// closes the panel (route→store no longer echoes this write to set it).
				urlDrivenOpenRef.current = true;
				depthRef.current = 1; // first pushed entry of this visit
				if (activeConversationId) {
					navigate({ pathname, search: searchWithBizy(search, CONVERSATION_VALUE) }, { state: { bizyConversationId: activeConversationId, bizyDepth: 1 } });
				} else {
					navigate({ pathname, search: searchWithBizy(search, CHAT_VALUE) }, { state: { bizyDepth: 1 } });
				}
			}
			return;
		}

		// Close transition: strip the param, but only a USER-INTENT close (the user
		// deliberately dismissing Bizy) pops history. The CURRENT entry's bizyDepth
		// says how many entries this Bizy visit pushed up to here (browser Back
		// mid-stack leaves the right depth on whatever entry is current), so a user
		// close pops exactly that many — landing on the pre-open page with no
		// overshoot, and Back after close can't reopen Bizy. Any other trigger
		// (`auto`, `navbar_collapse`, …) is a temporary/programmatic close that must
		// not rewrite the user's history position: replace-strip in place, which
		// still removes the param so route→store can't fight it back open. Depth 0
		// (cold load / pasted link / URL-driven open) also replace-strips. A
		// BIZY_ROUTE_TRIGGER close (browser Back already removed the param) hits
		// `!onBizyParam` and stays a no-op. Either way the pathname is unchanged,
		// so the user stays on the page they were on.
		if (!isOpen && prevIsOpen) {
			if (onBizyParam) {
				// Pop the whole visit off history using the SYNCHRONOUS counter, not the
				// (possibly-stale) per-entry read: after rapid paging the location lags, so
				// routeState.bizyDepth would undercount and land us back on a leftover
				// `?bizy=...` entry that route→store would reopen. (BIZY-703)
				const depth = depthRef.current;
				const isUserClose = USER_INTENT_TRIGGERS.has(lastTrigger ?? '');
				urlDrivenOpenRef.current = false;
				depthRef.current = 0;
				if (isUserClose && depth > 0) {
					navigate(-depth);
				} else {
					navigate({ pathname, search: searchWithoutBizy(search) }, { replace: true, state: {} });
				}
			}
			return;
		}

		// Deeper routing only while open and already on a `?bizy=` param.
		if (!isOpen || !onBizyParam) return;

		// Settings modal mirror (BIZY-701): reflect the store's modal state into the
		// param. Open pushes one entry (so Back collapses); a section change replaces
		// in place; close always replace-collapses to the underlying view. Runs before
		// the person/conversation branches because the modal renders over them.
		if (isSettingsModalOpen) {
			const settingsValue = searchWithBizy(search, bizySettingsValue(settingsSection));
			if (!prevSettingsOpen) {
				// Fresh open. Push a history entry unless the URL is already a settings
				// param (a route-driven open — nothing to add).
				if (target.kind !== 'settings') {
					const nextDepth = depthRef.current + 1;
					depthRef.current = nextDepth;
					navigate({ pathname, search: settingsValue }, { state: { ...routeState, bizyDepth: nextDepth } });
				}
			} else if (target.kind === 'settings' && target.section !== settingsSection) {
				// Section changed (tab click) → replace in place (depth unchanged). Guarded
				// to a settings URL with a different section so it's a no-op once the URL
				// already matches, and so a stale isSettingsModalOpen read right after a POP
				// off settings (before showChat closes the modal) can't rewrite a
				// non-settings URL back to settings.
				navigate({ pathname, search: settingsValue }, { replace: true, state: { ...routeState, bizyDepth: depthRef.current } });
			}
			return;
		}
		if (prevSettingsOpen && target.kind === 'settings') {
			// Closed via X/Escape while the panel stays open. (Browser Back moves the URL
			// off settings first, so it never reaches here.) Always replace-collapse to the
			// view still underneath. This is a no-op for that view in route→store (which is
			// store-authoritative + inbound-only), so no showChat() wipe and no echo. We do
			// NOT pop history: a pushed-entry pop heuristic mishandled forward-reopen (round
			// 3) and needed fragile tracking; the tradeoff is that Forward after close no
			// longer reopens the modal, which is acceptable.
			const depth = depthRef.current;
			if (personId) {
				navigate(
					{ pathname, search: searchWithBizy(search, bizyPersonValue(viewedPerson?.name)) },
					{ replace: true, state: { bizyPersonId: personId, ...(momentId ? { bizyObservedMomentId: momentId } : {}), bizyDepth: depth } }
				);
			} else if (activeConversationId) {
				navigate(
					{ pathname, search: searchWithBizy(search, CONVERSATION_VALUE) },
					{ replace: true, state: { bizyConversationId: activeConversationId, bizyDepth: depth } }
				);
			} else {
				navigate({ pathname, search: searchWithBizy(search, CHAT_VALUE) }, { replace: true, state: { bizyDepth: depth } });
			}
			return;
		}

		// Person view: push every card, including paging person→person AND paging
		// between two cards of the SAME person (the sidebar can list one person at
		// several moments), so each card is its own history entry and Back walks
		// them. A same-person moment change only counts as paging when BOTH moments
		// are concrete — the person view's newest-moment defaulting ('' → concrete
		// after a cold load) must not push. The id + moment live in navigation
		// state; the "already synced" check keys off them (not the cosmetic slug)
		// so Back/Forward — which restore them via state — never re-navigate.
		const personPaged = personId !== prevPersonId || Boolean(personId && momentId && prevMomentId && momentId !== prevMomentId);
		if (personId && personPaged) {
			const alreadySynced = target.kind === 'person' && routeState.bizyPersonId === personId && (routeState.bizyObservedMomentId ?? '') === (momentId ?? '');
			if (!alreadySynced) {
				// Push per card. Increment the synchronous counter (not the lagging
				// per-entry read) so rapid paging counts every entry — this is the fix for
				// the fullscreen close/reopen after fast paging. (BIZY-703)
				const nextDepth = depthRef.current + 1;
				depthRef.current = nextDepth;
				navigate(
					{ pathname, search: searchWithBizy(search, bizyPersonValue(viewedPerson?.name)) },
					{
						state: {
							bizyPersonId: personId,
							...(momentId ? { bizyObservedMomentId: momentId } : {}),
							bizyDepth: nextDepth,
						},
					}
				);
			}
			return;
		}
		if (!personId && prevPersonId && target.kind === 'person') {
			// Replace keeps the entry count (depth unchanged), so the collapsed-to entry
			// carries the same depth as the entry it replaces.
			navigate({ pathname, search: searchWithBizy(search, CHAT_VALUE) }, { replace: true, state: { bizyDepth: depthRef.current } });
			return;
		}

		// Conversation: push when a new one is opened; collapse to chat when cleared.
		// Skipped while a person is shown so a background conversation-id change
		// can't yank the URL off the person view.
		if (!personId && activeConversationId && activeConversationId !== prevConversationId) {
			const alreadySynced = target.kind === 'conversation' && routeState.bizyConversationId === activeConversationId;
			if (!alreadySynced) {
				const nextDepth = depthRef.current + 1;
				depthRef.current = nextDepth;
				navigate(
					{ pathname, search: searchWithBizy(search, CONVERSATION_VALUE) },
					{ state: { bizyConversationId: activeConversationId, bizyDepth: nextDepth } }
				);
			}
			return;
		}
		if (!activeConversationId && prevConversationId && target.kind === 'conversation') {
			// Replace keeps the entry count (depth unchanged), so depth carries through.
			navigate({ pathname, search: searchWithBizy(search, CHAT_VALUE) }, { replace: true, state: { bizyDepth: depthRef.current } });
		}
	}, [enabled, isOpen, viewedPerson, activeConversationId, isSettingsModalOpen, settingsSection, lastTrigger, location, navigate]);
}
